import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Check, Loader2, Sparkles, Crown, Zap } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

interface GoPremiumModalProps {
  open: boolean;
  onClose: () => void;
  trigger?: string;
}

const GoPremiumModal = ({ open, onClose, trigger }: GoPremiumModalProps) => {
  const { t } = useTranslation();
  const { user, isPremium } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState<string | null>(null);
  const [pendingPlan, setPendingPlan] = useState<string | null>(null);

  const is18Plus = localStorage.getItem("ayeris_is_18_plus") === "true";

  const plans = [
    {
      id: "free",
      name: t("goPremium.free"),
      price: "£0",
      period: "",
      features: t("goPremium.freeFeatures", { returnObjects: true }) as string[],
      current: true,
    },
    {
      id: "monthly",
      name: t("goPremium.monthly"),
      price: "£1.99",
      period: t("goPremium.perMonth"),
      features: t("goPremium.monthlyFeatures", { returnObjects: true }) as string[],
      popular: false,
    },
    {
      id: "lifetime",
      name: t("goPremium.lifetime"),
      price: "£10.99",
      period: t("goPremium.oneTime"),
      features: t("goPremium.lifetimeFeatures", { returnObjects: true }) as string[],
      popular: true,
    },
  ];

  const proceedToCheckout = async (plan: string) => {
    setLoading(plan);
    try {
      const { data, error } = await supabase.functions.invoke("create-premium-checkout", {
        body: { plan },
      });

      if (error) throw error;
      if (data?.url) {
        window.open(data.url, "_blank");
      } else {
        throw new Error("No checkout URL returned");
      }
    } catch (err: any) {
      toast.error(err.message || t("common.error"));
    } finally {
      setLoading(null);
    }
  };

  const handleUpgrade = async (plan: string) => {
    if (!user) {
      toast.error(t("common.loginRequired"));
      navigate("/auth");
      return;
    }

    if (!is18Plus) {
      setPendingPlan(plan);
      return;
    }

    proceedToCheckout(plan);
  };

  if (isPremium) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25 }}
            className="w-full max-w-sm bg-card border border-border rounded-2xl shadow-card overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="relative p-5 pb-3 text-center bg-gradient-to-b from-primary/10 to-transparent">
              <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-lg hover:bg-secondary transition-colors">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
              <div className="w-12 h-12 rounded-full bg-primary/15 mx-auto mb-2 flex items-center justify-center">
                <Crown className="w-6 h-6 text-primary" />
              </div>
              <h2 className="font-display text-xl font-bold text-foreground">{t("goPremium.title")}</h2>
              <p className="text-xs text-muted-foreground mt-1">
                {trigger === "group_limit" ? t("goPremium.groupLimitTrigger") : t("goPremium.unlockFull")}
              </p>
            </div>

            {/* Plans */}
            <div className="px-4 pb-4 space-y-2.5">
              {plans.map((plan) => (
                <div
                  key={plan.id}
                  className={`relative rounded-xl border-2 p-3 transition-all ${
                    plan.popular
                      ? "border-primary bg-primary/5 shadow-sm"
                      : "border-border bg-secondary/30"
                  }`}
                >
                  {plan.popular && (
                    <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2.5 py-0.5 rounded-full bg-primary text-primary-foreground text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                      <Sparkles className="w-3 h-3" /> {t("goPremium.mostPopular")}
                    </span>
                  )}

                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-display font-bold text-foreground text-sm">{plan.name}</p>
                      <p className="text-lg font-display font-bold text-foreground">
                        {plan.price}
                        {plan.period && (
                          <span className="text-xs font-normal text-muted-foreground ml-1">{plan.period}</span>
                        )}
                      </p>
                    </div>

                    {plan.current ? (
                      <span className="px-3 py-1.5 rounded-lg bg-secondary text-muted-foreground text-xs font-medium">
                        {t("common.current")}
                      </span>
                    ) : (
                      <button
                        onClick={() => handleUpgrade(plan.id)}
                        disabled={!!loading}
                        className={`px-4 py-2 rounded-xl text-xs font-semibold active:scale-95 transition-transform disabled:opacity-50 flex items-center gap-1.5 ${
                          plan.popular
                            ? "bg-gradient-iris text-primary-foreground shadow-iris"
                            : "bg-primary/15 text-primary"
                        }`}
                      >
                        {loading === plan.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Zap className="w-3.5 h-3.5" />
                        )}
                        {t("common.upgrade")}
                      </button>
                    )}
                  </div>

                  <div className="mt-2 space-y-1">
                    {plan.features.map((f) => (
                      <div key={f} className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
                        <Check className="w-3 h-3 text-primary shrink-0" />
                        {f}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      )}

      {/* Parental consent confirmation for under-18 users */}
      {pendingPlan && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 backdrop-blur-sm px-4"
          onClick={() => setPendingPlan(null)}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25 }}
            className="w-full max-w-sm bg-card border border-border rounded-2xl shadow-card p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center">
              <div className="w-12 h-12 rounded-full bg-primary/15 mx-auto mb-3 flex items-center justify-center">
                <Crown className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-display text-lg font-bold text-foreground">{t("goPremium.parentalConsentTitle")}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                {t("goPremium.parentalConsentMessage")}
              </p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPendingPlan(null)}
                className="flex-1 py-2.5 rounded-xl bg-secondary text-foreground text-sm font-medium active:scale-95 transition-transform"
              >
                {t("common.cancel")}
              </button>
              <button
                onClick={() => {
                  const plan = pendingPlan;
                  setPendingPlan(null);
                  proceedToCheckout(plan);
                }}
                disabled={!!loading}
                className="flex-1 py-2.5 rounded-xl bg-gradient-iris text-primary-foreground text-sm font-semibold shadow-iris active:scale-95 transition-transform disabled:opacity-50"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : t("common.confirm")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default GoPremiumModal;
